import React, { useEffect, useState } from 'react';

import { useTimer } from '../../context/TimerContext';

import { usePageControl } from '../../context/PageControlContext';
import PageNavigation from '../../components/PageNavigation';

import sha256 from "crypto-js/sha256";

import friendlybook from '../../assets/images/friendlybook.png';
import iconerror from '../../assets/images/icon-error.svg';
import iconsuccess from '../../assets/images/icon-success.svg';

function Module13() {

  // Page nav control
  const { canContinue, setCanContinue } = usePageControl();

  useEffect(() => {
    setCanContinue(false);
  }, [setCanContinue]);

  const handleSuccess = () => {
    setCanContinue(true);
  };

// SHA256 reference values
const correctHashes = {
  DOB: [
    "f18b8044e1069c884cdd5a8fdeff31156cee68c63fed3e8bf2aaec1e9d942022"
  ],

  "maiden-name": [
    "75d282e85804beeac19ce74b5a15de0621622f31fd4c5c1eaea3af7141ff09b2"
  ],

  "school-name": [
    "bb65bc08633bfc07b41b58b001103ad4de7c87bc67aa3197bb06a6455ef9d0cc",
    "5b011ce21bd79ac0beca2460d54004221906211a0a34c1d83aefd526325305fc",
    "10a8c1d72a708da51fed3e8235a29c6ae82ef714a8ed404bc89376e7f4648a28"
  ],

  "pet-name": [
    "471614e53afd4274ddbf324cfbfebc0c1368e4ca1b5807c798c2c8961ac7ca77"
  ],

  TOB: [
    "968c726a93761a40f740ec2496785691b4b4f480ce9b54ad500661065d985fd7"
  ]
};


// DOB helper utilities
const DOB_TEMPLATE = "dd/mm/yyyy";

function formatDOB(digits) {
  const padded = digits.padEnd(8, "_");
  return (
    padded.slice(0, 2) + "/" +
    padded.slice(2, 4) + "/" +
    padded.slice(4, 8)
  ).replace(/_/g, (m, i) => DOB_TEMPLATE[i]);
}

function stripNonDigits(value) {
  return value.replace(/\D/g, "").slice(0, 8);
}

//DOB handlers
const [dobDigits, setDobDigits] = useState("");
const [dobFocused, setDobFocused] = useState(false);

// DOB Change handler (numbers only)
const handleDOBChange = (e) => {
  const input = e.target;
  const rawDigits = stripNonDigits(input.value);

  if (rawDigits.length > 8) return;

  setDobDigits(rawDigits);

  setFormData(prev => ({
    ...prev,
    DOB: formatDOB(rawDigits)
  }));

setTouched(prev => ({
  ...prev,
  DOB: true
}));

  let newPos = nextEditablePos(input.selectionStart);
  setDobCaret(newPos);
};

// DOB Keydown handler
const handleDOBKeyDown = (e) => {
  const { key, target } = e;
  const start = target.selectionStart;
  const end = target.selectionEnd;

  // ----- DOB Clear all when a range is selected -----
  if ((key === "Backspace" || key === "Delete") && start !== end) {
    e.preventDefault();
    setDobDigits("");
    setFormData({ ...formData, DOB: DOB_TEMPLATE });
    setDobCaret(0);
    return;
  }

  // ----- DOB backspace handling with caret tracking -----
  if (key === "Backspace") {
    e.preventDefault();

    if (start === 0) return;

    const deletePos = prevEditablePos(start);

    const index =
      deletePos -
      (deletePos > 5 ? 2 : deletePos > 2 ? 1 : 0);

    const updated =
      dobDigits.slice(0, index) +
      dobDigits.slice(index + 1);

    setDobDigits(updated);
    setFormData(prev => ({
      ...prev,
      DOB: formatDOB(updated)
    }));
    setDobCaret(deletePos);
    return;
  }

  // ----- DOB allow navigation & control keys -----
  const allowedKeys = [
    "Delete",
    "ArrowLeft",
    "ArrowRight",
    "Tab"
  ];

  // ----- DOB block everything except digits -----
  if (!allowedKeys.includes(key) && !/^\d$/.test(key)) {
    e.preventDefault();
  }
};



// DOB Focus / blur behaviour
const handleDOBFocus = () => {
  setDobFocused(true);
  if (!dobDigits) {
    setFormData({ ...formData, DOB: DOB_TEMPLATE });
    setDobCaret(0);
  }
};

const handleDOBBlur = () => {
  setDobFocused(false);
  if (!dobDigits) {
    setFormData({ ...formData, DOB: "" });
  }
};

//DOB Auto-focus when the page loads
const dobRef = React.useRef(null);

useEffect(() => {
  dobRef.current?.focus();
}, []);

//DOB Track caret position
const [dobCaret, setDobCaret] = useState(0);

// DOB caret position helpers
const isSlash = (pos) => pos === 2 || pos === 5;

const nextEditablePos = (pos) =>
  isSlash(pos) ? pos + 1 : pos;

const prevEditablePos = (pos) =>
  isSlash(pos - 1) ? pos - 2 : pos - 1;

//DOB Restore caret position after render
useEffect(() => {
  if (dobFocused && dobRef.current) {
    dobRef.current.setSelectionRange(dobCaret, dobCaret);
  }
}, [dobCaret, dobFocused]);


// Form functionality
  const [formData, setFormData] = useState({
    DOB: "",
    "maiden-name": "",
    "school-name": "",
    "pet-name": "",
    TOB: ""
  });

  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [touched, setTouched] = useState({});

  const handleChange = (e) => {
    const { id, value } = e.target;

    setFormData(prev => ({
      ...prev,
      [id]: value
    }));

    setTouched(prev => ({
      ...prev,
      [id]: true
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    setTouched({});

    let newErrors = {};
    let allCorrect = true;

    for (const field in formData) {
      // Normalize: lowercase + strip whitespace, dots, and apostrophes
      const normalizedInput = formData[field]
      .trim()
      .toLowerCase()
      .replace(/[\s'.’]/g, '');

      // Hash the normalized input
      const hash = sha256(normalizedInput).toString();
      
      // Compare with stored hash
      if (
        !formData[field] ||
        !correctHashes[field].includes(hash)
      ) {
        newErrors[field] = true;
        allCorrect = false;
      }
    }

    setErrors(newErrors);
    setSubmitted(true);

    if (allCorrect) {
      goNext();
    }
  };

  const handleClear = () => {
    let cleared = { ...formData };
    let newErrors = {};

    for (const field in errors) {
      if (errors[field]) cleared[field] = "";
      newErrors[field] = false;
    }

    setFormData(cleared);
    setErrors(newErrors);
    setSubmitted(false);
  };

// Validation Icon Component
const ValidationIcon = ({ id, submitted, errors, touched, value }) => {
  if (!submitted || touched[id]) return null;

  if (errors[id]) {
    return (
      <div className="validation-alert validation-error">
        <img src={iconerror} className="icon-error" alt="Error icon" />
      </div>
    );
  }

  if (value) {
    return (
      <div className="validation-alert validation-success">
        <img src={iconsuccess} className="icon-success" alt="Success icon" />
      </div>
    );
  }

  return null;
};


const renderInput = (label, id, hint = "") => (
  <div className="row" key={id}>
    <div className="col-6">
      <label htmlFor={id}><strong>{label}</strong></label>
    </div>
    <div className="col-6">
      {id === "DOB" ? (
        <input
          ref={dobRef}
          id="DOB"
          type="text"
          autocomplete="off"
          inputMode="numeric"
          value={
            dobFocused || dobDigits
              ? formatDOB(dobDigits)
              : ""
          }
          onChange={handleDOBChange}
          onKeyDown={handleDOBKeyDown}
          onFocus={handleDOBFocus}
          onBlur={handleDOBBlur}
        />
      ) : (
        <input
          id={id}
          type="text"
          autocomplete="off"
          value={formData[id]}
          onChange={handleChange}
        />
      )}

      {hint && <p>{hint}</p>}

{/* Validation feedback */}
<ValidationIcon
  id={id}
  submitted={submitted}
  errors={errors}
  touched={touched}
  value={formData[id]}
/>

    </div>
  </div>
);


  // Timer
  const { timeRemaining, formatTime, isRunning } = useTimer();

  // Determine additional classes dynamically
  let additionalClasses = '';
    if (isRunning) {
      additionalClasses += ' timer-running';
    }
    if (timeRemaining <= 600 && timeRemaining >= 0) { // 10 mins or less but not negative
      additionalClasses += ' timer-warning';
    }
    if (timeRemaining < 0) { // time exceeded
      additionalClasses += ' timer-exceeded';
    } 

 // Go to next page
  const { goNext } = usePageControl();


  return (
    <div id="module-13" className="page physical">
        
        <header className="container-fluid">
            <div className="row">
                <div className="col-12">
                  <div id="CER"></div>
                  <div id="line"></div>
                  <div className={`timer-display${additionalClasses}`}>
                    <span className="timer-text">Time remaining <span className="timer-numbers">{formatTime(timeRemaining)}</span></span>
                  </div>
                </div>
            </div>
        </header>
    
        <main className="container-fluid">
            <div className="row">
                <div className="col-12">
                    <div id="box-wrapper-fb">
                        <div className="row">
                            <div className="col-12 text-center">
                              <img src={friendlybook} className="friendlybook-logo" alt="FriendlyBook logo"/>
                              <h1><span className="visually-hidden">FriendlyBook </span>Password Reset: Security Questions</h1>
                              {/* <p className="text-medium">Enter the correct details and press the Submit button.</p> */}
                            </div>
                        </div>
                         
    <form id="fb-security-questions-form" onSubmit={handleSubmit}>
      {renderInput("Date of birth", "DOB")} {/* Display instructions as a third comma seperated value, eg. "Enter in the format: dd/mm/yyyy" */}
      {renderInput("Mother's maiden name", "maiden-name")}
      {renderInput("Name of first school", "school-name")}
      {renderInput("First pet name", "pet-name")}
      {renderInput("Town of birth", "TOB")}

      <div className="row mt-2">
        <div className="col-12 text-center">
          {/*<button className="btn btn-fb btn-outline" type="button" onClick={handleClear}>Clear errors</button> */}
          <button className="btn btn-fb" type="submit">Submit</button>

          {submitted && Object.values(errors).some(Boolean) && (
            <div id="fb-security-questions-fail">
              <h2 className="mb-0">Not quite right. Please try again!</h2>
              {/* <h2>Details incorrrect!</h2>
              <p>Please correct the fields with an error message and submit again.</p> */}
            </div>
          )}

          {/* Client request: Go to the next page immediately if answer is correct (4 of 4) */}
          {/* {submitted && !Object.values(errors).some(Boolean) && (
            <div id="fb-security-questions-success">
              <h2>Success!</h2>
              <p>You can now continue.</p>
            </div>
          )} */}

        </div>
      </div>
    </form>

                    </div>
                </div>
            </div>
        </main>

        <footer className="container-fluid">
            <div className="row">
                <div className="col-12">
                    <PageNavigation />
                </div>
            </div>
        </footer>
    
    </div>
  );
};

export default Module13;
